import { List } from 'immutable';
import _ from 'lodash';

/**
 * Folds similar tickets into each other to reduce repetition
 * e.g. Two "1 Adult with 16-25" tickets would become "2 Adults with 16-25"
 *
 * @return {Immutable.List}
 */
export function getFoldedTickets (tickets, callback = _.identity) {
    return tickets
        .groupBy(callback)
        .reduce((newTickets, groupedTickets) => {
            const reducedTicket = groupedTickets.shift().reduce((newTicket, currentTicket) => {
                return newTicket.withMutations((ticket) => {
                    const addProps = (key) => newTicket.get(key) ?
                        newTicket.get(key) + currentTicket.get(key) :
                        newTicket.get(key);
                    return ticket
                        .set('adults', addProps('adults'))
                        .set('children', addProps('children'))
                        .set('price', addProps('price'))
                        .set('railcardDiscount', addProps('railcardDiscount'));
                });
            }, groupedTickets.first());
            return newTickets.push(reducedTicket);
        }, new List());
}

/**
 * Stamps each fare's tripId onto every leg of its outward/return journey.
 * Shared by Trip.fromJSON and order/Trip.fromJSON, which both need to
 * annotate raw journey legs before they're resolved into records.
 *
 * @param {Object} fares - raw json.fares object, keyed by fare name.
 * @return {Object} a new fares object with legs annotated with tripId.
 */
export function annotateFareLegsWithTripId (fares) {
    const faresObj = {};
    Object.entries(fares).forEach(([fareName, fare]) => {
        if (!fare) {
            faresObj[fareName] = null;
            return;
        }

        const outwardJourney = fare.outwardJourney
            ? {
                ...fare.outwardJourney,
                legs: fare.outwardJourney.legs ? fare.outwardJourney.legs.map((leg) => ({ ...leg, tripId: fare.tripId })) : [],
            }
            : null;
        const returnJourney = fare.returnJourney
            ? {
                ...fare.returnJourney,
                legs: fare.returnJourney.legs ? fare.returnJourney.legs.map((leg) => ({ ...leg, tripId: fare.tripId })) : [],
            }
            : null;

        faresObj[fareName] = { ...fare, outwardJourney, returnJourney };
    });
    return faresObj;
}
